import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FileText, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  Calendar,
  Clock,
  X,
  Image as ImageIcon
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from "axios";

const backendUrl = 'https://justurbane.in/backend_php/public';

const AdminArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [articleToSchedule, setArticleToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleHour, setScheduleHour] = useState('');
  const [scheduleMinute, setScheduleMinute] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const navigate = useNavigate();

  const categories = [
    'All Categories',
    'Fashion',
    'Business', 
    'Technology',
    'Finance',
    'Travel',
    'Health',
    'Culture',
    'Art',
    'Entertainment'
  ];

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchArticles();
  }, [navigate, currentPage, selectedCategory, searchTerm]);

  useEffect(() => {
    if (showScheduleModal) {
      const now = new Date();
      setScheduleDate(now.toISOString().split('T')[0]);
      
      const validHours = getAvailableHours(now.toISOString().split('T')[0]);
      const initialHour = validHours.length > 0 ? validHours[0] : '00';
      setScheduleHour(initialHour);
      
      const validMinutes = getAvailableMinutes(now.toISOString().split('T')[0], initialHour);
      setScheduleMinute(validMinutes.length > 0 ? validMinutes[0] : '00');
    }
  }, [showScheduleModal]);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      });

      if (selectedCategory && selectedCategory !== 'All Categories') {
        params.append('category', selectedCategory.toLowerCase());
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(
        `${backendUrl}/api/admin/articles?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        //console.log(data.articles);
        setArticles(data.articles);
        setTotalPages(data.total_pages);
      } else {
        throw new Error('Failed to fetch articles');
      }
    } catch (error) {
      toast.error('Failed to load articles');
      console.error('Articles error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (article) => {
    const confirmChange = window.confirm(
      `Are you sure you want to change the status of "${article.title}"?`
    );
    if (!confirmChange) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${backendUrl}/api/admin/articles/update-status/${article.slug}`,
        { 
          new_status: article.display_status === "enable" ? "disable" : "enable" 
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    
      if (response.data.success) {
        toast.success("Status updated successfully!");
        fetchArticles();
      } else {
        toast.error("Failed to update status.");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while updating status.");
    }
  };
    
  const handleDeleteArticle = async (articleId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${backendUrl}/api/admin/articles/${articleId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success('Article deleted successfully');
        fetchArticles();
        setShowDeleteModal(false);
        setArticleToDelete(null);
      } else {
        throw new Error('Failed to delete article');
      }
    } catch (error) {
      toast.error('Failed to delete article');
      console.error('Delete error:', error);
    }
  };

  const getAvailableHours = (selectedDateStr) => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const isToday = selectedDateStr === now.toISOString().split('T')[0];
    
    const hours = [];
    let startHour = 0;

    if (isToday) {
      if (currentMinute >= 50) {
        startHour = currentHour + 1;
      } else {
        startHour = currentHour;
      }
    }

    for (let i = startHour; i < 24; i++) {
      hours.push(String(i).padStart(2, '0'));
    }
    return hours;
  };

  const getAvailableMinutes = (selectedDateStr, selectedHourStr) => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const isToday = selectedDateStr === now.toISOString().split('T')[0];
    const isCurrentHour = parseInt(selectedHourStr) === currentHour;

    const allSlots = ['00', '10', '20', '30', '40', '50'];
    
    if (isToday && isCurrentHour) {
      return allSlots.filter(m => parseInt(m) > currentMinute);
    }
    
    return allSlots;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setScheduleDate(newDate);
    
    const validHours = getAvailableHours(newDate);
    if (!validHours.includes(scheduleHour)) {
      setScheduleHour(validHours[0] || '00');
      const validMinutes = getAvailableMinutes(newDate, validHours[0] || '00');
      setScheduleMinute(validMinutes[0] || '00');
    } else {
      const validMinutes = getAvailableMinutes(newDate, scheduleHour);
      if (!validMinutes.includes(scheduleMinute)) {
        setScheduleMinute(validMinutes[0] || '00');
      }
    }
  };

  const handleHourChange = (e) => {
    const newHour = e.target.value;
    setScheduleHour(newHour);
    const validMinutes = getAvailableMinutes(scheduleDate, newHour);
    if (!validMinutes.includes(scheduleMinute)) {
      setScheduleMinute(validMinutes[0] || '00');
    }
  };

  const openScheduleModal = (article) => {
    setArticleToSchedule(article);
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleDate || !scheduleHour || !scheduleMinute) {
      toast.error("Please select a valid date and time");
      return;
    }

    const now = new Date();
    const selectedDateTime = new Date(`${scheduleDate}T${scheduleHour}:${scheduleMinute}:00`);

    if (selectedDateTime <= now) {
      toast.error("Please select a future time");
      return;
    }

    const scheduledDateTime = `${scheduleDate} ${scheduleHour}:${scheduleMinute}:00`;
    
    setScheduling(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${backendUrl}/api/admin/articles/schedule`,
        {
          article_id: articleToSchedule.id,
          scheduled_at: scheduledDateTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`Article scheduled for ${scheduledDateTime}`);
        setShowScheduleModal(false);
        setArticleToSchedule(null);
        fetchArticles();
      } else {
        toast.error(response.data.message || "Failed to schedule article");
      }
    } catch (error) {
      console.error("Schedule error:", error);
      toast.error("An error occurred while scheduling");
    } finally {
      setScheduling(false);
    }
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper to format scheduled time cleanly
  const formatScheduledTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (article) => {
    if (article.featured) {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Featured</span>;
    }
    if (article.trending) {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Trending</span>;
    }
    if (article.premium) {
      return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Premium</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Standard</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">Article Management</h1>
            </div>
            
            <button
              onClick={() => navigate('/admin/articles/new')}
              className="flex items-center space-x-2 bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Article</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent appearance-none bg-white"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              {articles.length} articles found
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {articles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-12 h-12 cursor-pointer transition-transform hover:scale-105" onClick={() => openImageModal(article.hero_image)}>
                          {article.hero_image ? (
                            <img
                              src={article.hero_image}
                              alt=""
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                              <FileText className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <Link
                            to={`/article/${article.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded-md max-w-xs truncate inline-block hover:bg-blue-100 hover:underline"
                          >
                            {article.title}
                          </Link>
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {article.summary}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {article.category}
                         
                      </span>
                      {article.subcategory && (
                        <span className="text-xs text-gray-500 block">
                          {article.subcategory}
                          
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {article.author_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start space-y-2">
                        <button
                          onClick={() => handleStatusChange(article)}
                          className={`${
                            article.display_status === "enable"
                              ? "bg-green-500 hover:bg-green-600"
                              : "bg-yellow-500 hover:bg-yellow-600"
                          } text-white px-3 py-1 rounded transition-colors text-sm w-24 text-center`}
                        >
                          {article.display_status === "enable" ? "Published" : "Draft"}
                        </button>
                        
                        {article.display_status !== "enable" && (
                          // Logic: If already scheduled in future, show text. 
                          // If not scheduled OR schedule passed but not active, show button.
                          (() => {
                            const scheduledDate = article.scheduled_at ? new Date(article.scheduled_at) : null;
                            const isFutureSchedule = scheduledDate && scheduledDate > new Date();

                            if (isFutureSchedule) {
                              return (
                                <div className="flex flex-col items-center w-24">
                                   <span className="text-[10px] font-semibold text-purple-700 uppercase tracking-wide mb-0.5">
                                     Scheduled
                                   </span>
                                   <span className="text-[10px] text-gray-500 text-center leading-tight">
                                     {formatScheduledTime(article.scheduled_at)}
                                   </span>
                                </div>
                              );
                            } else {
                              return (
                                <button
                                  onClick={() => openScheduleModal(article)}
                                  className="flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-md transition-colors w-24 justify-center"
                                >
                                  <Calendar className="w-3 h-3" />
                                  <span>Schedule</span>
                                </button>
                              );
                            }
                          })()
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(article)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 text-gray-400 mr-1" />
                        {article.views || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(article.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/admin/articles/edit/${article.id}`)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setArticleToDelete(article);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t flex items-center justify-between sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full transform scale-100 transition-all shadow-xl">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Article</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete "{articleToDelete?.title}"? This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => handleDeleteArticle(articleToDelete.id)}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors shadow-sm"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setArticleToDelete(null);
                }}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors shadow-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl transform scale-100 transition-all">
            <div className="flex justify-between items-start mb-5">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Schedule Article</h3>
                  <p className="text-sm text-gray-500 truncate max-w-[200px]">
                    {articleToSchedule?.title}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowScheduleModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={scheduleDate}
                  onChange={handleDateChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hour</label>
                  <select
                    value={scheduleHour}
                    onChange={handleHourChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  >
                    {getAvailableHours(scheduleDate).map((h) => (
                      <option key={h} value={h}>
                        {h}:00
                      </option>
                    ))}
                    {getAvailableHours(scheduleDate).length === 0 && <option disabled>No hours left</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minute</label>
                  <select
                    value={scheduleMinute}
                    onChange={(e) => setScheduleMinute(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  >
                    {getAvailableMinutes(scheduleDate, scheduleHour).map((min) => (
                      <option key={min} value={min}>
                        {min}
                      </option>
                    ))}
                    {getAvailableMinutes(scheduleDate, scheduleHour).length === 0 && <option disabled>No slots left</option>}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">10-min Cron Interval</p>
                </div>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg flex items-start space-x-2">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                </div>
                <p className="text-xs text-blue-700">
                  Auto Scheduilng Enabled . You Can Schedule Auto Schedule In Interval of 10 minutes
                </p>
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={handleScheduleSubmit}
                disabled={scheduling || getAvailableHours(scheduleDate).length === 0}
                className="flex-1 bg-purple-600 text-white px-4 py-2.5 rounded-lg hover:bg-purple-700 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed font-medium"
              >
                {scheduling ? 'Scheduling...' : 'Confirm Schedule'}
              </button>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition-colors shadow-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[60] backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center">
            <button 
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={selectedImage} 
              alt="Full Preview" 
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminArticlesPage;